import { useRef, useState } from "react";

// PrimeReact Components
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

/**
 * Reusable Data Management Template
 * Based on the Orders UI design for consistent look and feel
 * 
 * @param {Object} props - Configuration object
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {Array} props.headerButtons - Array of button configurations
 * @param {Object} props.filterConfig - Filter configuration
 * @param {Array} props.columns - DataTable column configurations
 * @param {Array} props.data - Data array for the table
 * @param {Object} props.pagination - Pagination configuration
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onSearch - Search handler function
 * @param {Function} props.onPageChange - Pagination handler
 * @param {Function} props.onRowClick - Row click handler (not currently used to allow text selection)
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {React.Component} props.MobileCardsComponent - Mobile view component
 * @param {React.Component} props.emptyMessage - Custom empty state message
 */
function DataManagementTemplate({
  // Page Configuration
  title = "Data Management",
  description = "Manage your data efficiently",
  
  // Header Buttons Configuration
  headerButtons = [],
  
  // Filter Configuration
  filterConfig = null,
  showFilters = null,
  setShowFilters = null,
  
  // Data Table Configuration
  columns = [],
  data = [],
  loading = false,
  
  // Pagination Configuration
  pagination = {
    first: 0,
    rows: 20,
    total: 0,
    template: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
    currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} items"
  },
  
  // Event Handlers
  onSearch = () => {},
  onPageChange = () => {},
  onRowClick = () => {},
  
  // UI Configuration
  searchPlaceholder = "Search...",
  MobileCardsComponent = null,
  emptyMessage = "No data found"
}) {
  const searchRef = useRef();
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  
  // Use external filter state if provided, otherwise use internal state
  const filterState = showFilters !== null ? showFilters : internalShowFilters;
  const setFilterState = setShowFilters || setInternalShowFilters;

  const handleSearch = () => {
    if (searchRef.current && onSearch) {
      onSearch(searchRef.current.value);
    }
  };

  const handlePagination = (event) => {
    if (onPageChange) {
      onPageChange(event);
    }
  };

  return (
    <div className="p-6">
      {/* Header Card */}
      <Card className="mb-6 apollo-card content-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 apollo-heading" style={{ color: 'var(--heading-primary)' }}>{title}</h1>
            <p className="apollo-subheading" style={{ color: 'var(--heading-secondary)' }}>{description}</p>
          </div>
          
          {/* Header Buttons */}
          {headerButtons.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3">
              {headerButtons.map((button, index) => (
                <button
                  key={index}
                  onClick={button.onClick}
                  className={`inline-flex items-center px-4 py-2 font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${button.className || 'bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500'}`}
                >
                  {button.label}
                </button>
              ))}
              
              {/* Filter Button */}
              {filterConfig && (
                <div className="relative">
                  <button
                    onClick={() => setFilterState(!filterState)}
                    className="inline-flex items-center px-4 py-2 bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    Filter
                  </button>
                  
                  {filterState && filterConfig.dropdown && (
                    <div className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-300">
                      <div className="p-3 flex justify-between">
                        <div>
                          <p className="text-sm text-gray-700 font-semibold">Filter</p>
                        </div>
                        <button onClick={() => setFilterState(false)} className="text-gray-400 hover:text-gray-600">×</button>
                      </div>
                      {filterConfig.components && filterConfig.components.map((component, index) => (
                        <div key={index}>{component}</div>
                      ))}
                      <div className="p-3 flex justify-between">
                        <button
                          onClick={() => {
                            if (filterConfig.onReset) filterConfig.onReset();
                          }}
                          className="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          Reset All
                        </button>
                        <button
                          onClick={() => {
                            setFilterState(false);
                            if (filterConfig.onApply) filterConfig.onApply();
                          }}
                          className="ml-5 rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex flex-col lg:flex-row gap-4 mt-6">
          <div className="flex-1">
            <div className="flex rounded-lg border border-[#e2e8f0] bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all">
              <input
                type="text"
                placeholder={searchPlaceholder}
                ref={searchRef}
                className="flex-1 min-w-0 px-3 py-2 text-sm border-0 bg-transparent focus:ring-0 focus:outline-none placeholder:text-gray-400"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <button
                type="button"
                onClick={() => handleSearch()}
                className="px-4 py-2 bg-gray-50 border-l border-[#e2e8f0] text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
                Search
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table Card */}
      <Card className="apollo-card content-card">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ProgressSpinner 
              style={{ width: '50px', height: '50px' }} 
              strokeWidth="4" 
              fill="transparent" 
              animationDuration="1s"
            />
            <p className="text-body-2 apollo-body mt-4">Loading...</p>
          </div>
        ) : (
          <>
            {/* Desktop DataTable */}
            <div className="hidden lg:block">
              <DataTable
                value={data || []}
                emptyMessage={emptyMessage}
                paginator={false}
                className="p-datatable-custom apollo-table"
                tableStyle={{ minWidth: '50rem' }}
                headerClassName="apollo-table-header"
                rowClassName="apollo-table-row"
              >
                {columns.map((column, index) => (
                  <Column
                    key={index}
                    field={column.field}
                    header={column.header}
                    style={column.style}
                    body={column.body}
                    sortable={column.sortable || false}
                  />
                ))}
              </DataTable>
            </div>

            {/* Mobile Cards */}
            {MobileCardsComponent && (
              <div className="block lg:hidden">
                <MobileCardsComponent data={data} />
              </div>
            )}

            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="mt-6">
                <Paginator
                  first={pagination.first}
                  rows={pagination.rows}
                  totalRecords={pagination.total}
                  onPageChange={handlePagination}
                  template={pagination.template}
                  currentPageReportTemplate={pagination.currentPageReportTemplate}
                  className="justify-content-center"
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default DataManagementTemplate;
